import logger from '@libs/log';
import type Quill from 'quill';
import { forwardRef, useEffect, useLayoutEffect, useRef } from 'react';

const WYSIWYGEditor = (props: {
  value: string;
  height?: string | number;
  onBlur?: (text: string) => void;
  placeholder?: string;
}): JSX.Element => {
  const { value, onBlur, placeholder = 'Eg. Share your thoughts here...' } = props;
  const quillRef = useRef<Quill | null>(null);

  const handleTextChange = () => {
    if (quillRef.current) {
      const content = quillRef.current.root.innerHTML;
      onBlur?.(content);
    }
  };

  return (
    <div
      style={{
        border: `1px solid #d1d5db`,
        borderRadius: '10px',
      }}
    >
      <Editor
        key={`WYSIWYG-editor`}
        ref={quillRef}
        defaultValue={value}
        placeholder={placeholder}
        onBlur={handleTextChange}
        onTextChange={handleTextChange}
        autoHideToolbar={false}
      />
    </div>
  );
};

export default WYSIWYGEditor;

interface EditorProps {
  readOnly?: boolean;
  defaultValue?: string;
  onTextChange?: (delta: unknown, oldContents: unknown, source: string) => void;
  onSelectionChange?: (
    range: { index: number; length: number } | null,
    oldRange: { index: number; length: number } | null,
    source: string
  ) => void;
  onFocusIn?: () => void;
  onFocusOut?: () => void;
  onBlur?: (text: string) => void;
  placeholder?: string;
  height?: string | number;
  autoHideToolbar?: boolean;
}

const Editor = forwardRef<Quill, EditorProps>(
  (
    {
      readOnly,
      defaultValue,
      onTextChange,
      onSelectionChange,
      onFocusIn,
      onFocusOut,
      placeholder,
      autoHideToolbar = false,
    },
    ref
  ) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const defaultValueRef = useRef(defaultValue);
    const onTextChangeRef = useRef(onTextChange);
    const onSelectionChangeRef = useRef(onSelectionChange);
    const onFocusInRef = useRef(onFocusIn);
    const onFocusOutRef = useRef(onFocusOut);
    const quillInstanceRef = useRef<Quill | null>(null);
    const isToolbarClickedRef = useRef(false);

    useLayoutEffect(() => {
      onTextChangeRef.current = onTextChange;
      onSelectionChangeRef.current = onSelectionChange;
      onFocusInRef.current = onFocusIn;
      onFocusOutRef.current = onFocusOut;
    });

    useEffect(() => {
      if (ref && 'current' in ref && ref.current) {
        ref.current.enable(!readOnly);
      }
    }, [ref, readOnly]);

    // Function to toggle toolbar visibility
    const toggleToolbarVisibility = (show: boolean) => {
      if (containerRef.current && autoHideToolbar) {
        const toolbarElement = containerRef.current.querySelector('.ql-toolbar');
        if (toolbarElement) {
          (toolbarElement as HTMLElement).style.display = show ? 'block' : 'none';
        }
      }
    };

    useEffect(() => {
      // Import Quill dynamically to avoid SSR issues
      import('quill')
        .then((QuillModule) => {
          const Quill = QuillModule.default;

          if (!containerRef.current) return;

          const container = containerRef.current;
          // Clear any existing content first
          container.innerHTML = '';
          const editorContainer = container.appendChild(
            container.ownerDocument.createElement('div')
          );

          // Add custom class to the container for specific styling
          container.classList.add('wysiwyg-editor');

          // Configure Quill with similar toolbar options to TinyMCE, but without color options
          const quill = new Quill(editorContainer, {
            theme: 'snow',
            modules: {
              toolbar: [
                ['bold', 'italic', 'underline'],
                [{ size: [] }],
                [{ align: [] }],
                [{ list: 'ordered' }, { list: 'bullet' }],
                ['clean'],
              ],
            },
            placeholder: placeholder,
            readOnly: readOnly,
            formats: ['bold', 'italic', 'underline', 'size', 'align', 'list'], // Explicitly specify allowed formats
          });

          quillInstanceRef.current = quill;

          if (ref && 'current' in ref) {
            ref.current = quill;
          }

          if (defaultValueRef.current) {
            quill.clipboard.dangerouslyPasteHTML(defaultValueRef.current);
          }

          quill.on('text-change', (delta, oldContents, source) => {
            onTextChangeRef.current?.(delta, oldContents, source);
          });

          quill.on('selection-change', (range, oldRange, source) => {
            onSelectionChangeRef.current?.(range, oldRange, source);
          });

          // Hide toolbar initially if autoHideToolbar is true
          if (autoHideToolbar) {
            toggleToolbarVisibility(false);
          }

          // Add event listeners to the toolbar to prevent it from hiding when clicked
          const toolbarElement = container.querySelector('.ql-toolbar');
          if (toolbarElement) {
            toolbarElement.addEventListener('mousedown', (e) => {
              if (autoHideToolbar) {
                isToolbarClickedRef.current = true;
                e.preventDefault(); // Prevent default to avoid losing focus
              }
            });

            toolbarElement.addEventListener('mouseup', () => {
              // Reset after a short delay to allow the editor to regain focus
              if (autoHideToolbar) {
                setTimeout(() => {
                  isToolbarClickedRef.current = false;
                }, 100);
              }
            });
          }

          // Handle focus events
          editorContainer.addEventListener(
            'focus',
            () => {
              if (autoHideToolbar) {
                toggleToolbarVisibility(true);
              }
              onFocusInRef.current?.();
            },
            true
          );

          editorContainer.addEventListener(
            'blur',
            (e) => {
              // Only hide toolbar if autoHideToolbar is true and we're not clicking on the toolbar itself
              if (autoHideToolbar && !isToolbarClickedRef.current) {
                // Check if the related target is within the toolbar
                const toolbarElement = container.querySelector('.ql-toolbar');
                if (toolbarElement && !toolbarElement.contains(e.relatedTarget as Node)) {
                  toggleToolbarVisibility(false);
                  onFocusOutRef.current?.();
                }
              } else if (!autoHideToolbar) {
                onFocusOutRef.current?.();
              }
            },
            true
          );

          return () => {
            if (ref && 'current' in ref) {
              ref.current = null;
            }
            container.innerHTML = '';
          };
        })
        .catch((error: unknown) => {
          logger.error(error);
        });
    }, [ref, autoHideToolbar]);

    return <div ref={containerRef} className="wysiwyg-editor"></div>;
  }
);

Editor.displayName = 'Editor';
